'use client'

import { Star } from 'lucide-react'
import { useState, useRef, useEffect } from 'react'
import posthog from 'posthog-js'
import { useAuth } from '@/modules/auth/contexts/AuthContext'

interface BookmarkButtonProps {
  /** Mã HS đang xem, dùng để track event */
  hsId: string
  /** Ngữ cảnh hiển thị button */
  context: 'hs_detail' | 'hs_search'
  stopPropagation?: boolean
}

export default function BookmarkButton({
  hsId,
  context,
  stopPropagation = false,
}: BookmarkButtonProps) {
  const { user } = useAuth()
  const [isBookmarked, setIsBookmarked] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleClick = (e: React.MouseEvent) => {
    if (stopPropagation) {
      e.preventDefault()
      e.stopPropagation()
    }

    const next = !isBookmarked
    setIsBookmarked(next)

    // Chỉ track khi bookmark (action = bookmark)
    if (next) {
      posthog.capture('hs_code_bookmark_toggled', {
        user_id: user?.email || 'anonymous',
        page_url: window.location.href,
        page_path: window.location.pathname,
        action: 'bookmark',
        context,
        hs_id: hsId,
      })

      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => {
        setShowTooltip(true)
        timerRef.current = setTimeout(() => setShowTooltip(false), 2000)
      }, 200)
    } else {
      setShowTooltip(false)
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  return (
    <div className="relative shrink-0">
      <button
        onClick={handleClick}
        aria-label={isBookmarked ? 'Bỏ đánh dấu mã HS' : 'Đánh dấu mã HS'}
        className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-[#e5e5e5] bg-white transition-all hover:border-[#1E40AF] hover:bg-[#EFF6FF] active:scale-95"
      >
        <Star
          className={`size-[18px] transition-all ${isBookmarked ? 'text-[#F59E0B]' : 'text-[#737373] hover:text-[#1E40AF]'}`}
          fill={isBookmarked ? 'currentColor' : 'none'}
        />
      </button>
      {showTooltip && (
        <div className="absolute right-0 top-full z-10 mt-1.5 whitespace-nowrap rounded-lg border border-[#e5e5e5] bg-white px-3 py-2 text-[12px] font-medium text-[#525252] shadow-md">
          ✨ Tính năng sắp ra mắt
          <div className="absolute -top-1.5 right-3 size-3 rotate-45 border-l border-t border-[#e5e5e5] bg-white" />
        </div>
      )}
    </div>
  )
}
